import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import SectionDetail from "../../../components/SectionDetail/SectionDetail";
import TextButton from "../../../components/TextButton/TextButton";
import { useGetProposalDetailsQuery } from "../../../services/ProposalServices";
import { commaSeparatedPrice } from "../../../utils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Typography
      mb={1}
      sx={{
        fontWeight: 700,
        color: "var(--dark-grey-1)",
        fontSize: "16px",
      }}
      variant="subtitle1"
      component="p"
    >
      {children}
    </Typography>
  );
};

const PlanCard = () => {
  const { quote_data, product_data, proposal_data } = useGetProposalDetailsQuery();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const registrationYear = quote_data?.registration_date
    ? new Date(quote_data?.registration_date).getFullYear()
    : "";
  if (!proposal_data) return null;

  // Filter out addons with zero premium
  // const validAddons = proposal_data.ADDONS?.filter(
  //   (addon) => Number(addon.premium) > 0
  // ) || [];
  const validAddons = (proposal_data.ADDONS ?? []).filter(
    (addon) => !isNaN(Number(addon.premium)) && Number(addon.premium) > 0
  );

  const additionalGst = validAddons.reduce((acc, { premium }) => {
    const premiumValue = Number(premium);
    return acc + (premiumValue * 0.18) / 1.18;
  }, 0);

  // GST calculation (addons GST + base GST)
  // const additionalGst =
  //   validAddons.reduce((acc, curr) => {
  //     return acc + (Number(curr.premium) * 0.18) / 1.18;
  //   }, 0) || 0;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2} divider={<Divider />}>
        {/* Header */}
        <Box>
          <Box mb={1}>
            <TextButton
              color="primary"
              className="plan-card-change-insurer"
              onClick={() => {
                navigate(
                  `/quotes?quote_id=${proposal_data?.QUOTE_ID}&stage=PF`
                );
              }}
            >
              <ArrowBackIcon
                sx={{
                  height: "20px",
                  width: "20px",
                  mt: "-2px",
                }}
              />
              Change Insurer
            </TextButton>
          </Box>
          <img
            src={product_data?.LOGO_PATH}
            alt=""
            className="plancard-logo"
          />

          <Typography
            variant="h4"
            component="p"
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              mt: 1,
            }}
          >
            {product_data?.INSURANCE_NAME}
          </Typography>
        </Box>

        {/* Vehicle Details */}
        <Box>
          <SectionTitle>Vehicle Details</SectionTitle>
          <SectionDetail title="Manufacturer" value={quote_data?.vehicle_make} />
          <SectionDetail title="Model" value={quote_data?.vehicle_model} />
          <SectionDetail title="Version" value={quote_data?.vehicle_variant} />
          <SectionDetail title="IDV" value={commaSeparatedPrice(proposal_data?.IDV)} />
          {registrationYear !== currentYear && (<>
            <SectionDetail
              title="Previous Policy Expiry Date"
              value={quote_data?.existing_exp_date}
            /></>)}

        </Box>

        {/* Premium Breakup */}
        <Box>
          <SectionTitle>Premium Breakup</SectionTitle>
          <SectionDetail
            title="Basic OD Premium"
            value={commaSeparatedPrice(proposal_data?.BASIC_OD)}
          />
          <SectionDetail
            title="Basic TP Premium"
            value={commaSeparatedPrice(proposal_data?.BASIC_TP)}
          />
          <SectionDetail
            title="NCB Discount"
            value={commaSeparatedPrice(proposal_data?.NCB_DISCOUNT)}
          />

          {/* Only show valid addons */}
          {validAddons.map((addon) => {
            const basePremium = Number(addon.premium) / 1.18;
            return (
              <SectionDetail
                key={addon.slug}
                title={addon.name}
                value={commaSeparatedPrice(basePremium)}
              />
            );
          })}

          <SectionDetail
            title="GST"
            value={commaSeparatedPrice(
              Math.round(proposal_data?.GST + additionalGst)
            )}
          />
        </Box>

        {/* Policy Details */}
        <Box>
          <SectionTitle>Policy Details</SectionTitle>
          <SectionDetail
            title="Policy Type"
            value={product_data?.PRODUCT_NAME}
          />
        </Box>

        {/* Total Premium */}
        <Box>
          <SectionDetail
            main
            title="Premium"
            value={commaSeparatedPrice(proposal_data?.TOTAL_PREMIUM)}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export default PlanCard;
